import React from 'react'

import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Card } from '@amzn/stencil-react-components/card'
import { Col, Row } from '@amzn/stencil-react-components/layout'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { Status, StatusIndicator } from '@amzn/stencil-react-components/status-indicator'
import { Table, TableColumn } from '@amzn/stencil-react-components/table'
import { H2, Text } from '@amzn/stencil-react-components/text'

import {
    FIRST_DATA_LINE,
    ParsedSpreadsheet,
    pluralize,
    ROWS_PER_PAGE,
    RowValidation,
    truncateCell,
} from 'src/pages/import-content/ImportContentModel'

export interface ReviewStepProps {
    spreadsheet: ParsedSpreadsheet
    page: number
    onPageChange: (page: number) => void
    canPreview: boolean
    onPreviewRow: (row: Record<string, string>) => void
    isPreviewing: boolean
    canSubmit: boolean
    onSubmit: () => void
    onBack: () => void
    rowValidation?: RowValidation
    isValidatingRows: boolean
    validatedRowCount: number
}

type SpreadsheetRow = Record<string, string>

export const ReviewStep = ({
    spreadsheet,
    page,
    onPageChange,
    canPreview,
    onPreviewRow,
    isPreviewing,
    canSubmit,
    onSubmit,
    onBack,
    rowValidation,
    isValidatingRows,
    validatedRowCount,
}: ReviewStepProps) => {
    const pageStart = page * ROWS_PER_PAGE
    const pageCount = Math.ceil(spreadsheet.rows.length / ROWS_PER_PAGE)
    const pageRows = spreadsheet.rows.slice(pageStart, pageStart + ROWS_PER_PAGE)

    const errorsByRow = rowValidation?.byRow ?? {}
    const needsAttentionCount = Object.keys(errorsByRow).length
    const readyCount = spreadsheet.rowCount - needsAttentionCount

    const columns: TableColumn<SpreadsheetRow>[] = [
        {
            header: 'Row',
            width: '300px',
            cellComponent: ({ index }: { index: number }) => {
                const messages = errorsByRow[pageStart + index]
                return (
                    <Col gridGap='S100' dataTestId={`row-status-${pageStart + index}`}>
                        <Row alignItems='center' gridGap='S200'>
                            <Text fontSize='T100' fontWeight='bold' color='neutral90'>
                                Line {pageStart + index + FIRST_DATA_LINE}
                            </Text>
                            {isValidatingRows ? (
                                <StatusIndicator status={Status.Loading} messageText='Checking…' />
                            ) : (
                                <StatusIndicator
                                    status={messages ? Status.Negative : Status.Positive}
                                    messageText={messages ? 'Needs attention' : 'Ready'}
                                />
                            )}
                        </Row>
                        {messages?.map((message) => (
                            <Text key={message} fontSize='T100' color='red70'>
                                {message}
                            </Text>
                        ))}
                    </Col>
                )
            },
        },
        ...spreadsheet.headers.map((header) => ({
            header,
            cellComponent: ({ data }: { data: SpreadsheetRow }) => (
                <Text fontSize='T100' color='neutral90'>
                    {truncateCell(data[header])}
                </Text>
            ),
        })),
        {
            header: 'Preview',
            width: '96px',
            cellComponent: ({ data, index }: { data: SpreadsheetRow; index: number }) => (
                <Button
                    dataTestId={`preview-row-${pageStart + index}`}
                    size={ButtonSize.Small}
                    variant={ButtonVariant.Secondary}
                    aria-disabled={!canPreview || !!errorsByRow[pageStart + index]}
                    onClick={() => onPreviewRow(data)}
                >
                    Preview
                </Button>
            ),
        },
    ]

    return (
        <Card isElevated width='100%' dataTestId='import-content-review-step'>
            <Col gridGap='S300' width='100%'>
                <Row justifyContent='space-between' alignItems='center' gridGap='S300' width='100%'>
                    <H2 fontSize='T300'>Review your spreadsheet template</H2>
                    <Row alignItems='center' gridGap='S300'>
                        {isPreviewing && <Spinner dataTestId='import-content-spinner' />}
                        <Button
                            dataTestId='import-content-create'
                            variant={ButtonVariant.Primary}
                            aria-disabled={!canSubmit}
                            onClick={onSubmit}
                        >
                            Create
                        </Button>
                    </Row>
                </Row>
                <Text fontSize='T100' color='neutral70'>
                    Review the row entries before importing data into the Module Designer template.
                    Each row is validated against the template without creating anything; fix any
                    flagged row in the spreadsheet and upload it again. Line numbers match the
                    uploaded spreadsheet, where the header is line 1.
                </Text>

                <Row alignItems='center' gridGap='S300' dataTestId='review-summary'>
                    <Text fontSize='T200' fontWeight='bold'>
                        {pluralize(spreadsheet.rowCount, 'row')} to import
                    </Text>
                    {isValidatingRows ? (
                        <StatusIndicator
                            status={Status.Loading}
                            messageText={`Validating rows — ${validatedRowCount} of ${spreadsheet.rowCount} checked`}
                        />
                    ) : (
                        <>
                            <StatusIndicator
                                status={Status.Positive}
                                messageText={`${readyCount} ready`}
                            />
                            {needsAttentionCount > 0 && (
                                <StatusIndicator
                                    status={Status.Negative}
                                    messageText={`${needsAttentionCount} need${
                                        needsAttentionCount === 1 ? 's' : ''
                                    } attention`}
                                />
                            )}
                        </>
                    )}
                </Row>

                <Table
                    dataTestId='csv-rows'
                    aria-label='Spreadsheet rows to import'
                    columns={columns}
                    data={pageRows}
                    rowKeyAccessor={(_row: SpreadsheetRow, index: number) =>
                        String(pageStart + index)
                    }
                    hasInteractiveElement
                    shouldScrollHorizontally
                />

                {spreadsheet.rows.length > ROWS_PER_PAGE && (
                    <Row justifyContent='center' alignItems='center' gridGap='S300'>
                        <Button
                            dataTestId='csv-rows-prev'
                            size={ButtonSize.Small}
                            variant={ButtonVariant.Tertiary}
                            aria-disabled={page === 0}
                            onClick={() => onPageChange(page - 1)}
                        >
                            Previous
                        </Button>
                        <Text fontSize='T100' color='neutral70'>
                            Page {page + 1} of {pageCount}
                        </Text>
                        <Button
                            dataTestId='csv-rows-next'
                            size={ButtonSize.Small}
                            variant={ButtonVariant.Tertiary}
                            aria-disabled={page >= pageCount - 1}
                            onClick={() => onPageChange(page + 1)}
                        >
                            Next
                        </Button>
                    </Row>
                )}

                <Row>
                    <Button
                        dataTestId='import-content-back-to-upload'
                        variant={ButtonVariant.Tertiary}
                        onClick={onBack}
                    >
                        Back
                    </Button>
                </Row>
            </Col>
        </Card>
    )
}
